/** @jsxImportSource @emotion/react */
import { useMemo } from 'react'
import ReactSelect from 'react-select'
import { toast } from 'react-toastify'

import {
  useGetEffectivePreferredDmeOfficeQuery,
  useUpdateLabPhysicianPreferredDmeOfficeMutation,
} from 'store/services/labPhysician'
import { useGetLabOfficesQuery } from 'store/services/office'

interface IProps {
  physicianNpi: string
  preferredDmeOfficeId?: number | null
  onSaved: () => void
}

type OfficeOption = {
  value: number | 'none' | 'separator'
  label: string
  isDisabled?: boolean
}

const NONE_OPTION: OfficeOption = { value: 'none', label: 'None' }
const SEPARATOR: OfficeOption = { value: 'separator' as any, label: '─────────────────', isDisabled: true }

export const PreferredDmeOfficeSection = ({
  physicianNpi,
  preferredDmeOfficeId,
  onSaved,
}: IProps) => {
  const { data: offices = [], isLoading: isLoadingOffices } = useGetLabOfficesQuery({})
  const { data: effective } = useGetEffectivePreferredDmeOfficeQuery({ physicianNpi })
  const [updateLabPhysicianPreferredDmeOffice, { isLoading: isUpdatingPreferredDme }] =
    useUpdateLabPhysicianPreferredDmeOfficeMutation()

  const officeOptions = useMemo<OfficeOption[]>(
    () =>
      offices
        .filter((office) => office.isDmeOffice && office.officeName)
        .sort((left, right) => String(left.officeName).localeCompare(String(right.officeName)))
        .map((office) => ({
          value: office.id,
          label: `${office.officeName} (${office.addressCity}, ${office.addressState})`,
        })),
    [offices],
  )

  const allOptions: OfficeOption[] = [NONE_OPTION, SEPARATOR, ...officeOptions]

  const physicianOwnId = effective?.physicianPreferredDmeOfficeId ?? preferredDmeOfficeId ?? null
  const isInheritedNone = effective?.noneExplicitlySet === true && effective.source === 'group'
  const isInherited = physicianOwnId == null && !effective?.noneExplicitlySet && effective?.source === 'group'
  const displayedId = isInherited ? effective?.groupPreferredDmeOfficeId ?? null : physicianOwnId

  const selectedValue: OfficeOption | null =
    effective?.noneExplicitlySet
      ? NONE_OPTION
      : officeOptions.find((option) => Number(option.value) === Number(displayedId)) ?? null

  const handlePreferredDmeChange = async (option: OfficeOption | null) => {
    const isNone = option?.value === 'none'
    try {
      await updateLabPhysicianPreferredDmeOffice({
        npi: physicianNpi,
        preferredDmeOfficeId: isNone ? null : (option?.value as number) ?? null,
        isNone,
      }).unwrap()
      toast.success(isNone ? 'Preferred DME set to None.' : option ? 'Preferred DME office updated.' : 'Preferred DME office cleared.')
      onSaved()
    } catch (error) {
      console.error('Error updating preferred DME office:', error)
      toast.error('Failed to update preferred DME office.')
    }
  }

  return (
    <div className="card card-primary mt-3">
      <div className="row p-3">
        <div className="col-sm-12">
          <h4 className="mb-3">Preferred DME Office</h4>
        </div>

        <div className="col-sm-12 col-lg-6">
          <label className="form-label">Preferred DME Office</label>
          <ReactSelect
            className="basic-single"
            classNamePrefix="select"
            isClearable
            isSearchable
            isDisabled={isInherited || isInheritedNone}
            isLoading={isLoadingOffices || isUpdatingPreferredDme}
            placeholder="Search DME offices..."
            value={selectedValue}
            onChange={(option) => handlePreferredDmeChange(option as OfficeOption | null)}
            options={allOptions}
            menuPortalTarget={document.body}
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 10000 }) }}
          />
          {isInherited && (
            <small className="text-muted d-block mt-1">
              Inherited from group. Selecting a value sets a physician-specific override; clearing
              it reverts to the group's preferred DME office.
            </small>
          )}
          {isInheritedNone && (
            <small className="text-muted d-block mt-1">
              No preferred DME (inherited from group).
            </small>
          )}
        </div>
      </div>
    </div>
  )
}
